import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Paper,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Divider,
  Card,
  CardContent,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
} from '@mui/material';
import { hasWritePermission } from '../../utils/menuFilter';
import {
  Search,
  Payment,
  AttachMoney,
  Home,
  Person,
  Business,
  CheckCircle,
  Receipt,
  AccountBalance,
} from '@mui/icons-material';

const AllotmentPayment = () => {
  const canWriteAllotment = hasWritePermission('plot-booking.allotment-payment') || hasWritePermission('booking.allotment-payment');
  const [searchQuery, setSearchQuery] = useState('');
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    balanceAmount: '',
    paymentMode: '',
    manualReceiptNumber: '',
    paymentAmount: '',
    paymentDate: '',
    receivedBy: '',
    givenBy: '',
    cashDepositeBy: '',
    narration: '',
    isPayoutRecover: 'no',
  });
  const [errors, setErrors] = useState({});
  const [searchLoading, setSearchLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);

  // Sample payment details data - this would come from API
  const [paymentDetails] = useState({
    customerId: 'CUST001',
    customerName: 'John Doe',
    associateId: 'ASC001',
    plotStatus: 'Allotted',
    paymentPlan: 'EMI Plan',
    site: 'Green Valley',
    plotNo: 'P101',
    plotRate: '₹4,500/sq ft',
    plotSize: '1200 sq ft',
    bookingId: 'BK001',
    bookingDate: '2025-09-15',
    allotmentDate: '2025-10-01',
    balanceAllotmentAmount: '₹8,50,000',
  });

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setErrors({ searchQuery: 'Please enter Plot No or Booking ID' });
      return;
    }

    setSearchLoading(true);
    setErrors({});

    // Simulate API call
    setTimeout(() => {
      setShowPaymentDetails(true);
      setSuccessMessage('');
      // Set balance amount from payment data
      setPaymentForm({
        ...paymentForm,
        balanceAmount: paymentDetails.balanceAllotmentAmount,
      });
      console.log('Searching for:', searchQuery);
      setSearchLoading(false);
    }, 1500);
  };

  const handlePaymentInputChange = (field, value) => {
    setPaymentForm({
      ...paymentForm,
      [field]: value,
    });
    // Clear errors when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleOpenPaymentDialog = () => {
    setPaymentDialogOpen(true);
  };

  const handleClosePaymentDialog = () => {
    setPaymentDialogOpen(false);
    setErrors({});
  };

  const validatePaymentForm = () => {
    const newErrors = {};

    if (!paymentForm.paymentAmount.trim()) {
      newErrors.paymentAmount = 'Payment amount is required';
    }

    if (!paymentForm.paymentMode.trim()) {
      newErrors.paymentMode = 'Payment mode is required';
    }

    if (!paymentForm.paymentDate.trim()) {
      newErrors.paymentDate = 'Payment date is required';
    }

    return newErrors;
  };

  const handleSubmitPayment = async () => {
    const validationErrors = validatePaymentForm();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitLoading(true);

    // Simulate API call
    setTimeout(() => {
      setSuccessMessage(`Allotment Payment of ${paymentForm.paymentAmount} has been submitted successfully for ${paymentDetails.bookingId}`);
      setPaymentDialogOpen(false);
      setShowPaymentDetails(false);
      setSearchQuery('');
      // Reset payment form
      setPaymentForm({
        balanceAmount: paymentDetails.balanceAllotmentAmount,
        paymentMode: '',
        manualReceiptNumber: '',
        paymentAmount: '',
        paymentDate: '',
        receivedBy: '',
        givenBy: '',
        cashDepositeBy: '',
        narration: '',
        isPayoutRecover: 'no',
      });
      setErrors({});
      console.log('Payment form data:', paymentForm);
      setSubmitLoading(false);
    }, 2000);
  };

  const paymentModeOptions = [
    'Cash',
    'Cheque',
    'NEFT/RTGS',
    'Online Transfer',
    'UPI',
    'Credit Card',
    'Debit Card',
  ];

  return (
    <Box sx={{ p: 3, backgroundColor: '#fafafa', minHeight: 'auto' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
          Allotment Payment Management
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Process and manage allotment payments for plot bookings
        </Typography>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 4, borderRadius: 2 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircle />
            {successMessage}
          </Box>
        </Alert>
      )}

      {/* Search Section */}
      <Card sx={{ mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <Search sx={{ color: '#1976d2', fontSize: 28 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
              Search Plot/Booking Details
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Plot No / Booking ID"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Enter Plot Number or Booking ID"
                error={!!errors.searchQuery}
                helperText={errors.searchQuery}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Receipt color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleSearch}
                disabled={searchLoading || !searchQuery.trim()}
                startIcon={<Search />}
                sx={{
                  height: 56,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '15px',
                  boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)',
                  borderRadius: 2
                }}
              >
                {searchLoading ? 'Searching...' : 'Search'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Payment Details */}
      {showPaymentDetails && (
        <Card sx={{ mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <AttachMoney sx={{ color: '#f57c00', fontSize: 28 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
                Allotment Payment Details
              </Typography>
            </Box>

            <Typography variant="body2" color="primary" sx={{ mb: 3, fontWeight: 600 }}>
              Plot/Booking Details for Allotment Payment Processing
            </Typography>

            {/* Customer Information */}
            <Card sx={{ p: 3, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1976d2' }}>
                Customer Information
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Customer ID</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                      {paymentDetails.customerId}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Customer Name</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#000' }}>
                      {paymentDetails.customerName}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Associate ID</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#000' }}>
                      {paymentDetails.associateId}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Plot Status</Typography>
                    <Chip
                      label={paymentDetails.plotStatus}
                      color="success"
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Card>

            {/* Plot Information */}
            <Card sx={{ p: 3, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1976d2' }}>
                Plot Information
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Plot Number</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                      {paymentDetails.plotNo}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Plot Size</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#000' }}>
                      {paymentDetails.plotSize}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Booking ID</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                      {paymentDetails.bookingId}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Plot Rate</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#f57c00' }}>
                      {paymentDetails.plotRate}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Booking Date</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#000' }}>
                      {paymentDetails.bookingDate}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Allotment Date</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#000' }}>
                      {paymentDetails.allotmentDate}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Card>

            {/* Financial Summary */}
            <Card sx={{ p: 3, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1976d2' }}>
                Financial Summary
              </Typography>
              <Box textAlign="center" sx={{ p: 3, backgroundColor: '#ffebee', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Balance Allotment Amount
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#f44336' }}>
                  {paymentDetails.balanceAllotmentAmount}
                </Typography>
              </Box>
            </Card>

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              {canWriteAllotment && (
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleOpenPaymentDialog}
                  startIcon={<Payment />}
                  sx={{
                    px: 6,
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '15px',
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                    borderRadius: 2
                  }}
                >
                  Process Allotment Payment
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Payment Dialog */}
      <Dialog
        open={paymentDialogOpen}
        onClose={handleClosePaymentDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{
          fontSize: '24px',
          fontWeight: 700,
          color: '#000',
          borderBottom: '1px solid #e0e0e0',
          pb: 2
        }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Payment sx={{ color: '#1976d2', fontSize: 28 }} />
            Allotment Payment Form
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Balance Amount"
                variant="outlined"
                fullWidth
                value={paymentForm.balanceAmount}
                onChange={(e) => handlePaymentInputChange('balanceAmount', e.target.value)}
                disabled
                helperText="Current outstanding balance amount"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoney color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.paymentMode}>
                <InputLabel>Payment Mode *</InputLabel>
                <Select
                  value={paymentForm.paymentMode}
                  label="Payment Mode *"
                  onChange={(e) => handlePaymentInputChange('paymentMode', e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  {paymentModeOptions.map((mode) => (
                    <MenuItem key={mode} value={mode}>
                      {mode}
                    </MenuItem>
                  ))}
                </Select>
                {errors.paymentMode && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {errors.paymentMode}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Manual Receipt Number"
                variant="outlined"
                fullWidth
                value={paymentForm.manualReceiptNumber}
                onChange={(e) => handlePaymentInputChange('manualReceiptNumber', e.target.value)}
                placeholder="Enter manual receipt number"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Receipt color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Payment Amount *"
                variant="outlined"
                fullWidth
                value={paymentForm.paymentAmount}
                onChange={(e) => handlePaymentInputChange('paymentAmount', e.target.value)}
                placeholder="Enter payment amount"
                error={!!errors.paymentAmount}
                helperText={errors.paymentAmount || "Enter the allotment payment amount"}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoney color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Payment Date *"
                type="date"
                variant="outlined"
                fullWidth
                value={paymentForm.paymentDate}
                onChange={(e) => handlePaymentInputChange('paymentDate', e.target.value)}
                error={!!errors.paymentDate}
                helperText={errors.paymentDate}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Received By"
                variant="outlined"
                fullWidth
                value={paymentForm.receivedBy}
                onChange={(e) => handlePaymentInputChange('receivedBy', e.target.value)}
                placeholder="Name of person who received payment"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Given By"
                variant="outlined"
                fullWidth
                value={paymentForm.givenBy}
                onChange={(e) => handlePaymentInputChange('givenBy', e.target.value)}
                placeholder="Name of person who gave payment"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Cash Deposited By"
                variant="outlined"
                fullWidth
                value={paymentForm.cashDepositeBy}
                onChange={(e) => handlePaymentInputChange('cashDepositeBy', e.target.value)}
                placeholder="Name of person who deposited cash"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccountBalance color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Narration"
                variant="outlined"
                fullWidth
                multiline
                rows={3}
                value={paymentForm.narration}
                onChange={(e) => handlePaymentInputChange('narration', e.target.value)}
                placeholder="Enter payment narration/remarks..."
                helperText="Additional remarks or notes about the payment"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormLabel component="legend" sx={{ fontWeight: 600, color: '#1976d2', mb: 2 }}>
                  Is Payout Recover
                </FormLabel>
                <RadioGroup
                  row
                  value={paymentForm.isPayoutRecover}
                  onChange={(e) => handlePaymentInputChange('isPayoutRecover', e.target.value)}
                >
                  <FormControlLabel
                    value="yes"
                    control={<Radio />}
                    label="Yes"
                    sx={{ fontWeight: 600 }}
                  />
                  <FormControlLabel
                    value="no"
                    control={<Radio />}
                    label="No"
                    sx={{ fontWeight: 600 }}
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button
            onClick={handleClosePaymentDialog}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            Cancel
          </Button>
          {canWriteAllotment && (
            <Button
              onClick={handleSubmitPayment}
              color="primary"
              variant="contained"
              disabled={!paymentForm.paymentAmount || !paymentForm.paymentMode || !paymentForm.paymentDate || submitLoading}
              startIcon={<Payment />}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
              }}
            >
              {submitLoading ? 'Processing...' : 'Submit Payment'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AllotmentPayment;